"use client"
import { useCallback, useMemo, type MouseEvent as ReactMouseEvent } from "react"
import {
  getHoveredRegionId,
  type HoveredPowerItem,
} from "../../../../power-bubble-chart/hoveredPowerItem"
import {
  type PowerLabId,
  type PowerRegionData,
  type PowerRegionId,
} from "../../../../power-bubble-chart/powerBubbleChartData"
import { labIconById } from "../../../../power-bubble-chart/powerChartIcons"
import { PowerTreemapCell } from "./PowerTreemapCell"
import { useAnimationTuning } from "@/app/playbook/animation/animationTuningContext"
import {
  layoutComputeFragmentsLinear,
  type ComputeFragment,
  type ComputeFragmentRect,
} from "./powerTreemapLayout"
import { layoutComputeFragmentsStable } from "./stableTreemap"
import { pruneSmallToRest } from "./pruneTreemap"
import { BLOCK_GAP_PX } from "./powerTreemapStyle"
import { useTreemapTransition } from "./useTreemapTransition"

export type PowerCellLayoutMode = "treemap" | "linear"

// The country group holding the most total compute this year. Region-ranked
// cell treatments highlight it (solid fill) and outline the rest.
const getTopComputeRegionId = (
  regions: PowerRegionData[]
): PowerRegionId | null => {
  let topRegionId: PowerRegionId | null = null
  let topCompute = -Infinity
  for (const region of regions) {
    let regionCompute = 0
    for (const lab of region.labs) regionCompute += Math.max(0, lab.compute)
    if (regionCompute > topCompute) {
      topCompute = regionCompute
      topRegionId = region.id
    }
  }
  return topRegionId
}

// 0-based rank of every country group by total compute (0 = most compute,
// 1 = second, …). Region-ranked treatments (e.g. the per-group dither
// lower-HUD) read this to vary the fill by how much power a group holds.
const getRegionComputeRanking = (
  regions: PowerRegionData[]
): Record<PowerRegionId, number> => {
  const totals = regions.map(region => {
    let regionCompute = 0
    for (const lab of region.labs) regionCompute += Math.max(0, lab.compute)
    return { id: region.id, compute: regionCompute }
  })
  totals.sort((a, b) => b.compute - a.compute)
  const ranking = {} as Record<PowerRegionId, number>
  totals.forEach((entry, index) => {
    ranking[entry.id] = index
  })
  return ranking
}
// Frontier holders with no brand mark (the anonymous "US Co N"-style labs)
// fall back to the generic AI-company logo — but only the biggest
// MAX_FALLBACK_LOGO_LABS of them (by compute, across the whole treemap) show
// it; the rest render plain so the same generic mark doesn't wallpaper the
// chart. Labs with a real brand mark are unaffected.
const MAX_FALLBACK_LOGO_LABS = 2
const getFallbackLogoLabIds = (fragments: ComputeFragment[]): Set<string> =>
  new Set(
    fragments
      .filter(f => f.lab.borderOnly !== true && labIconById[f.lab.id as PowerLabId] == null)
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_FALLBACK_LOGO_LABS)
      .map(f => f.lab.id),
  )
import {
  type GetCellLogoTreatment,
  type GetTreemapCellStyle,
} from "./powerTreemapStyle"
import { type TreemapCornerLayout } from "./powerTreemapStyleCorner"
import { type BlockSize } from "./useMeasuredPowerBlock"

// Lays all labs into one continuous rectangle so relative compute shares are visible.
export const PowerTreemap = ({
  regions,
  blockSize,
  hoveredItem,
  labTransparencyLevel,
  onHoverItem,
  getCellStyle,
  getLogoTreatment,
  layoutMode = "treemap",
  groupByCountry = true,
  countryGroupGapPx,
  gapPx,
  borderRadiusPx,
  logoSizeFraction,
  includeNonFrontier = true,
  cornerLayout,
}: {
  regions: PowerRegionData[]
  blockSize: BlockSize
  hoveredItem: HoveredPowerItem | null
  labTransparencyLevel: number | undefined
  onHoverItem: (item: HoveredPowerItem) => void
  getCellStyle?: GetTreemapCellStyle
  getLogoTreatment?: GetCellLogoTreatment
  layoutMode?: PowerCellLayoutMode
  groupByCountry?: boolean
  countryGroupGapPx?: number
  gapPx?: number
  borderRadiusPx?: number
  // Forwarded to each cell: when set, sizes the lab icon to this fraction of
  // the cell's smaller dimension instead of the default clamped logo size.
  logoSizeFraction?: number
  includeNonFrontier?: boolean
  // When set, cells render the corner layout (icon on a black square top-left,
  // share number top-right). See CORNER_CELL_TREATMENT.
  cornerLayout?: TreemapCornerLayout
}) => {
  // Anonymous per-region size-rank SLOTS (identity-free), so a churning org set
  // can't teleport cells. The target is pruned so no company renders under 8px —
  // sub-8px companies roll into their "Rest of X" block (see pruneSmallToRest) —
  // which keeps the animation free of sliver churn.
  const fragments = useMemo(
    () => pruneSmallToRest(regions, includeNonFrontier, blockSize.width, blockSize.height, 8, gapPx ?? BLOCK_GAP_PX),
    [regions, includeNonFrontier, blockSize.width, blockSize.height, gapPx],
  )
  // The layout is passed to the transition engine as a function so it can be
  // re-run every frame on interpolated compute values (see useTreemapTransition).
  const layout = useCallback(
    (frags: ComputeFragment[]): ComputeFragmentRect[] => (
      layoutMode === "linear"
        ? layoutComputeFragmentsLinear(frags, 0, 0, blockSize.width, blockSize.height, groupByCountry, countryGroupGapPx)
        : layoutComputeFragmentsStable(frags, 0, 0, blockSize.width, blockSize.height)
    ),
    [blockSize.height, blockSize.width, layoutMode, groupByCountry, countryGroupGapPx]
  )
  // Continuous transition engine: interpolates compute VALUES and re-lays-out
  // every frame, so each frame is one valid tiling (no overlap ever) and cells
  // grow/shrink smoothly. See useTreemapTransition. Disabled → snap to layout.
  const tuning = useAnimationTuning()
  const transition = useTreemapTransition(fragments, layout, {
    durationMs: tuning.treemapLayoutMs,
    easing: tuning.treemapLayoutEasing,
    labelFadeMs: tuning.treemapLabelPhaseMs,
  })
  const hoveredRegionId = getHoveredRegionId(hoveredItem, regions)
  const hoveredLabId = hoveredItem?.kind === "lab" ? hoveredItem.id : null
  const topRegionId = useMemo(() => getTopComputeRegionId(regions), [regions])
  const regionRankById = useMemo(() => getRegionComputeRanking(regions), [regions])
  const fallbackLogoLabIds = useMemo(() => getFallbackLogoLabIds(fragments), [fragments])
  const cells = transition.cells
  // Hover is resolved GEOMETRICALLY, on one pointer surface over the whole
  // block, instead of per-cell mouseenter events: enter events miss when the
  // pointer lands in the inter-cell gaps, and they race the column-level
  // "chart" claim (ClaimPowerHoverOnEnter — now a functional `prev ?? chart`
  // update so the race can't clobber the lab hover). The handler hit-tests
  // the pointer against the live layout rects (nearest rect when the point
  // sits in a gap), so any pointer event inside the block resolves to a lab.
  // It listens on BOTH mousemove and mouseover: when a takeover overlay
  // (e.g. the globe's datacenter list) fades out beneath a stationary
  // pointer, the browser re-dispatches only over/enter events — a
  // mousemove-only surface would leave the entry cell without its hover
  // until the pointer moved again.
  const handlePointer = (event: ReactMouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - bounds.left
    const y = event.clientY - bounds.top
    let nearest: ComputeFragmentRect | null = null
    let nearestDistanceSq = Infinity
    for (const cell of cells) {
      // Cells collapsed to ~0 mid-morph paint nothing and shouldn't hover.
      if (cell.width < 0.5 || cell.height < 0.5) continue
      const dx = Math.max(cell.x - x, 0, x - (cell.x + cell.width))
      const dy = Math.max(cell.y - y, 0, y - (cell.y + cell.height))
      const distanceSq = dx * dx + dy * dy
      if (distanceSq < nearestDistanceSq) {
        nearestDistanceSq = distanceSq
        nearest = cell
      }
    }
    if (!nearest) return
    const labId = nearest.fragment.lab.id as PowerLabId
    if (hoveredItem?.kind === "lab" && hoveredItem.id === labId) return
    onHoverItem({ kind: "lab", id: labId })
  }
  return (
    <div
      className="absolute inset-0"
      onMouseMove={handlePointer}
      onMouseOver={handlePointer}
    >
      {cells.map(cell => (
        <PowerTreemapCell
          key={cell.fragment.id}
          rect={cell}
          blockSize={blockSize}
          hoveredRegionId={hoveredRegionId}
          hoveredLabId={hoveredLabId}
          topRegionId={topRegionId}
          regionRankById={regionRankById}
          labTransparencyLevel={labTransparencyLevel}
          fallbackLogoEligible={fallbackLogoLabIds.has(cell.fragment.lab.id)}
          getCellStyle={getCellStyle}
          getLogoTreatment={getLogoTreatment}
          gapPx={gapPx}
          borderRadiusPx={borderRadiusPx}
          logoSizeFraction={logoSizeFraction}
          cornerLayout={cornerLayout}
          labelOpacity={cell.labelOpacity}
        />
      ))}
    </div>
  )
}
